import React, { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { BanknotesIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ClipLoader from 'react-spinners/ClipLoader';
import { getAllPenalities } from '../penalities/services/penalities';
import PenalitieDetailModal from '../../Shared/PenalitieDetailModal';

const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDateTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    if (isNaN(d.getTime())) return String(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const statusBadge = (idStatus) => {
    switch (idStatus) {
        case 1:
            return { label: 'PENDIENTE POR VALIDAR', cls: 'bg-yellow-100 text-yellow-800' };
        case 2:
            return { label: 'RECHAZADO', cls: 'bg-red-100 text-red-800' };
        case 3:
            return { label: 'APROBADO', cls: 'bg-green-100 text-green-800' };
        default:
            return { label: 'DESCONOCIDO', cls: 'bg-gray-100 text-gray-800' };
    }
};

const buildVoucherSrc = (voucher) => {
    if (!voucher) return null;
    if (typeof voucher !== 'string') return null;
    if (voucher.startsWith('data:')) return voucher;
    if (/^https?:\/\//i.test(voucher) || voucher.startsWith('/')) return voucher;
    return `data:image/*;base64,${voucher}`;
};

const Row = ({ label, value }) => (
    <div className="flex justify-between items-start py-1.5">
        <span className="text-sm text-gray-600 mr-4">{label}:</span>
        <span className="text-sm font-semibold text-gray-900 text-right break-all">{value || 'N/A'}</span>
    </div>
);

const PaymentDetailModal = ({ open, onClose, payment }) => {
    const [loadingFines, setLoadingFines] = useState(false);
    const [relatedFines, setRelatedFines] = useState([]);
    const [selectedFine, setSelectedFine] = useState(null);
    const [voucherOpen, setVoucherOpen] = useState(false);

    useEffect(() => {
        if (!open || !payment) {
            setRelatedFines([]);
            return;
        }
        const idDetailFines = Array.isArray(payment.idDetailFine) ? payment.idDetailFine : [];
        if (idDetailFines.length === 0) {
            setRelatedFines([]);
            return;
        }
        setLoadingFines(true);
        Promise.all(idDetailFines.map((idDetailFine) => getAllPenalities({ idDetailFine, status: -1 })))
            .then((results) => {
                const fines = [];
                const seen = new Set();
                results.forEach((result) => {
                    if (!result || result.status !== 200) return;
                    const payload = result.data?.data;
                    const items = Array.isArray(payload)
                        ? payload
                        : Array.isArray(payload?.data)
                            ? payload.data
                            : [];
                    items.forEach((item) => {
                        (item.headerFine || []).forEach((fine) => {
                            if (seen.has(fine.idHeaderFine)) return;
                            seen.add(fine.idHeaderFine);
                            fines.push({
                                ...fine,
                                identificationUser: item.identification,
                                fullnameUser: `${item.firstName || ''} ${item.middleName || ''}`.trim(),
                                email: item.email,
                                userEmail: item.email,
                                phone: item.phone ? item.phone[0] : null,
                            });
                        });
                    });
                });
                setRelatedFines(fines);
            })
            .catch(() => setRelatedFines([]))
            .finally(() => setLoadingFines(false));
    }, [open, payment]);

    if (!payment) return null;

    const badge = statusBadge(payment.idStatus);
    const voucherSrc = buildVoucherSrc(payment.voucher);

    return (
        <>
            <Dialog open={open && !selectedFine} onClose={onClose} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in z-40"
                />
                <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <DialogPanel
                            transition
                            className="relative transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all w-full max-w-3xl h-[calc(100vh-2rem)] flex flex-col"
                        >
                            <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex-shrink-0">
                                <div className="flex items-center">
                                    <BanknotesIcon className="h-6 w-6 text-gray-600 mr-3" />
                                    <h3 className="text-lg font-semibold text-gray-900">Detalles del Pago</h3>
                                    <span className={`ml-auto inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge.cls}`}>
                                        {badge.label}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="ml-3 rounded-full p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>

                            <div className="px-6 py-6 flex-1 overflow-y-auto space-y-6">
                                <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                                    <h4 className="text-base font-semibold text-gray-900 mb-3">Datos del Pago</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                                        <Row label="Identificación" value={payment.identification} />
                                        <Row label="Nombre" value={payment.name} />
                                        <Row label="Teléfono" value={payment.phone} />
                                        <Row label="Monto" value={payment.amount != null ? `Bs. ${payment.amount}` : null} />
                                        <Row label="Banco emisor" value={`${payment.bankCode || ''} ${payment.bank ? '- ' + payment.bank : ''}`.trim()} />
                                        <Row label="Banco receptor" value={`${payment.bankCodeReceiving || ''} ${payment.bankReceiving ? '- ' + payment.bankReceiving : ''}`.trim()} />
                                        <Row label="Referencia" value={payment.reference} />
                                        <Row label="Fecha transferencia" value={formatDate(payment.dateTrans)} />
                                        <Row label="Fecha registro" value={payment.dateReg ? formatDate(payment.dateReg) : null} />
                                        <Row label="Tipo de pago" value={payment.paymentTypeDescrip} />
                                        <Row label="Origen" value={payment.paymentFromDescrip} />
                                        <Row label="Usuario registro" value={payment.userName} />
                                        <Row label="Usuario estatus" value={payment.userNameStatus} />
                                    </div>
                                    {payment.comment && (
                                        <div className="mt-4 bg-white rounded p-3 border border-gray-200">
                                            <p className="text-sm font-medium text-gray-600 mb-1">Comentario del pago</p>
                                            <p className="text-sm text-gray-900">{payment.comment}</p>
                                        </div>
                                    )}
                                    {payment.commentStatus && (
                                        <div className="mt-3 bg-white rounded p-3 border border-gray-200">
                                            <p className="text-sm font-medium text-gray-600 mb-1">Comentario del estatus</p>
                                            <p className="text-sm text-gray-900">{payment.commentStatus}</p>
                                        </div>
                                    )}
                                    {voucherSrc && (
                                        <div className="mt-4">
                                            <p className="text-sm font-medium text-gray-600 mb-2">Comprobante (voucher)</p>
                                            <button
                                                type="button"
                                                onClick={() => setVoucherOpen(true)}
                                                className="inline-block"
                                            >
                                                <img
                                                    src={voucherSrc}
                                                    alt="Voucher de pago"
                                                    className="max-h-48 rounded-lg border border-gray-300 shadow-sm hover:opacity-90 transition-opacity"
                                                />
                                            </button>
                                        </div>
                                    )}
                                </section>

                                <section>
                                    <h4 className="text-base font-semibold text-gray-900 mb-3">
                                        Multas Asociadas
                                        {Array.isArray(payment.idDetailFine) && (
                                            <span className="ml-2 text-sm font-normal text-gray-500">
                                                ({payment.idDetailFine.length} ID{payment.idDetailFine.length !== 1 ? 's' : ''} de detalle)
                                            </span>
                                        )}
                                    </h4>
                                    {loadingFines ? (
                                        <div className="py-8 flex justify-center">
                                            <ClipLoader color="#1e40af" size={32} />
                                        </div>
                                    ) : relatedFines.length === 0 ? (
                                        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 text-sm text-gray-600 text-center">
                                            No se encontraron multas asociadas a este pago.
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {relatedFines.map((fine) => (
                                                <div key={fine.idHeaderFine} className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                                                    <div className="flex-1">
                                                        <p className="text-sm text-gray-500">Código</p>
                                                        <p className="font-semibold text-gray-900">{fine.code}</p>
                                                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                                            <div><span className="text-gray-500">Estatus: </span><span className="font-medium">{fine.descripStatus}</span></div>
                                                            <div><span className="text-gray-500">Monto: </span><span className="font-medium">Bs. {fine.totalBS}</span></div>
                                                            <div><span className="text-gray-500">Placa: </span><span className="font-medium">{fine.numberPlate || 'N/A'}</span></div>
                                                            <div><span className="text-gray-500">Restante: </span><span className="font-medium">Bs. {fine.remainder}</span></div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedFine(fine)}
                                                        className="inline-flex items-center px-3 py-2 text-sm font-semibold text-white bg-blue-950 rounded-md shadow-sm hover:bg-blue-900"
                                                    >
                                                        <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                                                        Ver detalle
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </section>
                            </div>

                            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="inline-flex justify-center items-center px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>

            <PenalitieDetailModal
                open={!!selectedFine}
                penalitie={selectedFine}
                onClose={() => setSelectedFine(null)}
            />

            <Dialog open={voucherOpen} onClose={() => setVoucherOpen(false)} className="relative z-[60]">
                <DialogBackdrop className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-40" />
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <DialogPanel className="relative max-w-5xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-auto">
                        <button
                            type="button"
                            onClick={() => setVoucherOpen(false)}
                            className="absolute top-2 right-2 rounded-full bg-white/90 p-2 text-gray-700 hover:bg-white shadow"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                        {voucherSrc && <img src={voucherSrc} alt="Voucher ampliado" className="max-w-full h-auto" />}
                    </DialogPanel>
                </div>
            </Dialog>
        </>
    );
};

export default PaymentDetailModal;
